import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';

/**
 * Inline message block — BUILD-PLAN §41/§44.
 *
 * Used for form-level outcomes that are not tied to a single field, such as
 * a failed sign-in or a confirmation that a reset email was sent.
 */

type Tone = 'error' | 'success' | 'info';

const TONES: Record<Tone, { classes: string; Icon: typeof Info; role: 'alert' | 'status' }> = {
  // Errors interrupt (role="alert"); success and info do not (role="status"),
  // so assistive tech announces them without cutting off the user.
  error: {
    classes: 'border-danger bg-danger/5 text-danger',
    Icon: AlertCircle,
    role: 'alert',
  },
  success: {
    classes: 'border-success bg-success/5 text-success',
    Icon: CheckCircle2,
    role: 'status',
  },
  info: {
    classes: 'border-line bg-surface-muted text-ink',
    Icon: Info,
    role: 'status',
  },
};

interface AlertProps {
  tone?: Tone;
  children: ReactNode;
}

export function Alert({ tone = 'info', children }: AlertProps) {
  const { classes, Icon, role } = TONES[tone];

  return (
    <div
      role={role}
      className={`flex items-start gap-2.5 rounded-sm border px-4 py-3 text-sm ${classes}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}
